import { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { Alert, Avatar, Button, Card, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, notification } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import AxiosUtils from '../../utils/axios-utils';
import { API } from '../../service/api-route';
import { ColumnsType } from 'antd/lib/table';
import { formatedDate } from '../../utils/common';
import { useNavigate, useParams } from 'react-router-dom';
import { SERVER_FILE_ENDPOINT } from '../../constants/envs';
import PageTitle from '../../components/page-title';
import AddMembersModal from '../../components/add-members-modal';
import { addMembersToClan, ClanMember, moveMemberToClan, removeMemberFromClan } from '../domestic-manager/api';

interface GroupOption { id: number; title: string }

function GroupDomesticDetailManager() {
  const { id } = useParams();
  const groupId = Number(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<ClanMember[]>([]);
  const [infoGroup, setInfoGroup] = useState<any>(null);
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [moving, setMoving] = useState<ClanMember | null>(null);
  const [targetGroupId, setTargetGroupId] = useState<number | undefined>();
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('ALL');

  const refresh = useCallback(() => setReload((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    AxiosUtils.get(`${API.createGroup}/${groupId}`)
      .then((res: any) => { if (active) setInfoGroup(res.data); })
      .catch(() => { if (active) setInfoGroup(null); });
    return () => { active = false; };
  }, [groupId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    AxiosUtils.get(API.domesticMembers(groupId))
      .then((res: any) => { if (active) setMembers(res.data || []); })
      .catch(() => {
        if (active) {
          setMembers([]);
          notification.error({ message: 'Không tải được thành viên nhóm' });
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [groupId, reload]);

  useEffect(() => {
    if (!moving) return;
    AxiosUtils.get(API.createGroup + '/domestic')
      .then((res: any) => setGroups(res.data || []))
      .catch(() => notification.error({ message: 'Không tải được danh sách nhóm' }));
  }, [moving]);

  const removeMember = async (member: ClanMember) => {
    setSaving(true);
    try {
      await removeMemberFromClan(groupId, member.key);
      notification.success({ message: 'Đã gỡ thành viên khỏi nhóm' });
      refresh();
    } catch (error: any) {
      notification.error({ message: error?.message || 'Không thể gỡ thành viên' });
    } finally {
      setSaving(false);
    }
  };

  const moveMember = async () => {
    if (!moving || !targetGroupId) return;
    setSaving(true);
    try {
      await moveMemberToClan(groupId, moving.key, targetGroupId);
      notification.success({ message: 'Đã chuyển thành viên sang nhóm khác' });
      setMoving(null);
      setTargetGroupId(undefined);
      refresh();
    } catch (error: any) {
      notification.error({ message: error?.message || 'Không thể chuyển thành viên' });
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<ClanMember> = [
    { title: 'ID app', dataIndex: 'userId', key: 'userId', width: 85, render: (value) => value || '—' },
    {
      title: 'TikTok ID', key: 'username', width: 215,
      render: (_, member) => <Tooltip title={member.username}><span>{member.username ? `@${member.username}` : '—'}</span></Tooltip>,
    },
    { title: 'UID TikTok', dataIndex: 'uid', key: 'uid', width: 210, render: (value) => value || '—' },
    {
      title: 'Nguồn / Trạng thái', key: 'source', width: 290,
      render: (_, member) => (
        <Space direction="vertical" size={2}>
          <Tag color={member.source === 'MOVED' ? 'gold' : member.source === 'FILE' ? 'blue' : 'default'}>
            {member.source === 'MOVED' ? 'Chuyển nhóm' : member.source === 'FILE' ? 'Từ file TikTok' : 'Thêm thủ công'}
          </Tag>
          {member.fileGroupName && <Typography.Text type="secondary" className={styles.sourceName} title={member.fileGroupName}>
            File: {member.fileGroupName}
          </Typography.Text>}
          {member.movedFromGroupId && <Typography.Text type="secondary">
            Chuyển từ nhóm #{member.movedFromGroupId}
          </Typography.Text>}
        </Space>
      ),
    },
    { title: 'Tài khoản app', key: 'email', width: 260, ellipsis: { showTitle: false },
      render: (_, member) => <Tooltip title={member.email}>{member.email || <Typography.Text type="secondary">Chưa liên kết</Typography.Text>}</Tooltip> },
    { title: 'Ngày tạo', dataIndex: 'createdAt', key: 'createdAt', width: 125,
      render: (value) => value ? formatedDate(value) : '—' },
    {
      title: 'Thao tác', key: 'action', width: 170,
      render: (_, member) => {
        const isOwner = member.userId === infoGroup?.owner?.id;
        return <Space size={0} className={styles.actions}>
          <Button type="link" size="small" disabled={isOwner || saving} onClick={() => { setMoving(member); setTargetGroupId(undefined); }}>Chuyển</Button>
          <Popconfirm title={`Gỡ ${member.username || member.uid || member.email} khỏi nhóm?`}
            description="Nếu có trong file TikTok, thành viên sẽ không tự xuất hiện lại sau lần nhập file tiếp theo."
            okText="Gỡ" cancelText="Hủy" okButtonProps={{ danger: true }}
            onConfirm={() => removeMember(member)} disabled={isOwner || saving}>
            <Button type="link" size="small" danger disabled={isOwner || saving}>Gỡ</Button>
          </Popconfirm>
        </Space>;
      },
    },
  ];

  const clanName = infoGroup?.title || `Nhóm #${id}`;
  const visibleMembers = members.filter((member) => {
    const query = search.trim().toLowerCase();
    return (sourceFilter === 'ALL' || member.source === sourceFilter) &&
      (!query || [member.username, member.uid, member.email, member.fileGroupName]
        .some((value) => value?.toLowerCase().includes(query)));
  });
  return <div>
    <PageTitle title="Chi tiết nhóm" extra={
      <Space wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/domestic-management')}>Danh sách nhóm</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddMembersOpen(true)}>Thêm</Button>
      </Space>
    } />

    <Card className={styles.infoCard}>
      <Space size={16} align="center" wrap>
        <Avatar shape="square" size={64}
          src={infoGroup?.avatar ? `${SERVER_FILE_ENDPOINT}/${infoGroup.avatar}` : undefined} icon={<TeamOutlined />} />
        <div>
          <Typography.Title level={4} className={styles.clanName}>{clanName}</Typography.Title>
          <Space size={16} wrap>
            <Typography.Text type="secondary">Trưởng nhóm: <Typography.Text strong>{infoGroup?.owner?.nickName || infoGroup?.owner?.username || '—'}</Typography.Text></Typography.Text>
            <Typography.Text type="secondary">Thành viên: <Typography.Text strong>{members.length}</Typography.Text></Typography.Text>
            {infoGroup?.tiktokGroupName && <Typography.Text type="secondary">Nhóm trong file: <Typography.Text strong>{infoGroup.tiktokGroupName}</Typography.Text></Typography.Text>}
          </Space>
          {infoGroup?.description && <Typography.Paragraph type="secondary" className={styles.description}>{infoGroup.description}</Typography.Paragraph>}
        </div>
      </Space>
    </Card>

    <Alert type="info" showIcon className={styles.note}
      message="Danh sách gồm Creator theo UID mới nhất trong file TikTok và tài khoản được thêm thủ công. Chuyển nhóm sẽ gỡ khỏi nhóm cũ; nhãn Chuyển nhóm đánh dấu idol đã chuyển hoặc khác nhóm trong file. Số liệu TikTok gốc không đổi." />
    <Space wrap className={styles.filters}>
      <Input.Search placeholder="Tìm TikTok ID, UID, email hoặc nhóm trong file"
        allowClear value={search} onChange={(event) => setSearch(event.target.value)} className={styles.search} />
      <Select value={sourceFilter} onChange={setSourceFilter} style={{ width: 180 }}
        options={[
          { value: 'ALL', label: 'Tất cả nguồn' },
          { value: 'FILE', label: 'Từ file TikTok' },
          { value: 'MOVED', label: 'Chuyển nhóm' },
          { value: 'MANUAL', label: 'Thêm thủ công' },
        ]} />
      {(search || sourceFilter !== 'ALL') && <Typography.Text type="secondary">
        {visibleMembers.length} / {members.length} thành viên
      </Typography.Text>}
    </Space>
    <div className="custom-table custom-form">
      <Table columns={columns} rowKey="key" dataSource={visibleMembers} loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{ x: 1355 }} locale={{ emptyText: 'Chưa có thành viên từ file TikTok hoặc được thêm thủ công' }} />
    </div>

    <AddMembersModal open={isAddMembersOpen} title="Thêm thành viên nhóm"
      targetLabel="Nhóm quản lý" target={{ value: groupId, label: clanName }}
      userLabel="Thành viên" submitText="Thêm vào nhóm"
      userHint="Tìm TikTok ID, email hoặc UID (tối thiểu 2 ký tự). Tài khoản ở nhóm khác sẽ chuyển sang nhóm này."
      existingUserIds={members.map((member) => member.userId).filter((value): value is number => value !== null)}
      onSubmit={(userIds, selectedGroupId) => addMembersToClan(selectedGroupId, userIds)}
      onSuccess={() => { setIsAddMembersOpen(false); refresh(); }} onCancel={() => setIsAddMembersOpen(false)} />

    <Modal title="Chuyển thành viên sang nhóm" open={!!moving}
      onCancel={() => { if (!saving) { setMoving(null); setTargetGroupId(undefined); } }}
      onOk={moveMember} okText="Chuyển nhóm" okButtonProps={{ disabled: !targetGroupId, loading: saving }}>
      <Typography.Paragraph>
        {moving?.username ? `@${moving.username}` : moving?.uid || moving?.email} sẽ rời nhóm {clanName} và chỉ xuất hiện ở nhóm được chọn.
      </Typography.Paragraph>
      <Select showSearch placeholder="Chọn nhóm đích" style={{ width: '100%' }} value={targetGroupId}
        optionFilterProp="label" onChange={setTargetGroupId}
        options={groups.filter((group) => group.id !== groupId).map((group) => ({ value: group.id, label: group.title }))} />
    </Modal>
  </div>;
}

export default GroupDomesticDetailManager;
